import React, { useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CustomInput } from 'Douleutaras/components/CustomInput/CustomInput';
import { CustomLabel } from 'Douleutaras/components/CustomLabel/CustomLabel';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { useLocalization } from 'Douleutaras/hooks/localization';
import { Controller, useFormContext } from 'react-hook-form';
import { emailPattern, phoneNumber } from 'Douleutaras/components/Forms/Validators';
import { ToggleField } from 'Douleutaras/components/Forms/Fields/ToggleField';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { getCategoryTos } from 'Douleutaras/redux/data/categories/categoriesTosSlice';
import ModalTextInputItem from 'Douleutaras/components/modals/parts/ModalTextInputItem';
import ModalSelectionItem from 'Douleutaras/components/modals/parts/ModalSelectionItem';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { rhfValidateFn } from 'Douleutaras/components/Forms/v2/Validators';
import { selectMe } from 'Douleutaras/redux/data/users/meSlice';
import { useAB } from 'Douleutaras/app/lib/ab';
import { PostHogEventNames } from 'Douleutaras/app/lib/posthog/posthog.provider';
import { useFlowParam } from 'Douleutaras/hooks/jobs/jobFlow';
import { POST_JOB_FLOW } from 'Douleutaras/views/job/post-job/types';
import { FLOW_TYPE_TO_EVENT_PROP } from 'Douleutaras/constants/job';

import './UserStep.scss';

export interface CheckUserResponse {
  result: 'ok' | 'existing';
  marketing_consent_granted: boolean;
  // The below properties exist when "result = existing"
  password?: boolean;
  email_verified?: boolean;
  email?: boolean;
  seller?: boolean;
  has_phone?: boolean;
}

const EMAIL_CHECK_THROTTLE_MS = 1000; // Delay and throttle email check request to prevent flood
let timeoutThrottle; // Hold timeout handle in order to throttle email check requests

interface UserStepProps {
  autoDealIds: Array<string>;
  categoryId: string;
  postJobSettings: {
    EXISTING_USER_CONTACT_DETAILS_STEP: boolean;
  };
  userCheckResponse: CheckUserResponse;
  setUserCheckResponse(userCheckResponse: CheckUserResponse): void;
}

const UserStep = ({ autoDealIds, categoryId, postJobSettings, userCheckResponse, setUserCheckResponse }: UserStepProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const API_URL = COUNTRY_CONFIG.API_URL[(useLocalization()).countryCode];
  const { control, register, setValue, trigger, watch, formState: { errors, isValid, touchedFields } } = useFormContext();
  const categoriesTos = useAppSelector(state => state.categoriesTos);
  const [ tosOpen, setTosOpen ] = useState<boolean>(false);
  const userFromStorage = useAppSelector(selectCurrentUser);
  const user = useAppSelector(selectMe);
  const userIsLogged = Boolean(user);
  const userHasCheckResponse = userCheckResponse !== undefined;
  const loggedContactEnabled = postJobSettings.EXISTING_USER_CONTACT_DETAILS_STEP;
  const ab = useAB();
  const postingFlowResponse = useAppSelector(state => state.pros.postingFlow);
  const flowParam = useFlowParam();
  const flowType = flowParam === POST_JOB_FLOW.MULTI_BID
    ? FLOW_TYPE_TO_EVENT_PROP.MULTI_SELECT
    : FLOW_TYPE_TO_EVENT_PROP.SINGLE_SELECT;

  useEffect(() => {
    dispatch(getCategoryTos({ categoryId }));
    // @ab PostHog event
    ab.capture(PostHogEventNames.PERSONAL_DETAILS_VIEWED, {
      category_slug: postingFlowResponse.category_slug,
      sub_category_slug: postingFlowResponse.sub_category_slug,
      business_unit_slug: postingFlowResponse.business_unit_slug,
      pro_selection_flow_type: flowType,
      bids_objective: postingFlowResponse.max_bids
    });
  }, []);

  const checkEmail = async (emailValue: string): Promise<void> => {
    if (!emailPattern.test(emailValue)) {
      return;
    }
    const userCheckResponseResource: AxiosResponse<CheckUserResponse> = await axios.post(`${API_URL}api/public/login/check_user/`,
      {
        email: emailValue
      },
      {
        params: {
          format: 'json'
      }
    });
    setUserCheckResponse(userCheckResponseResource.data);
    timeoutThrottle = undefined;
  };

  const fieldProps = register('email', {
    required: t('T.POST-JOB.EMAIL REQUIRED'),
    validate: value => {
      return userCheckResponse !== undefined || Boolean(userIsLogged);
    },
    pattern: {
      value: emailPattern,
      message: t('T.POST-JOB.VALID EMAIL REQUIRED')
    }
  });

  useEffect(() => {
    if (userCheckResponse !== undefined) {
      trigger('email');
    }
  }, [userCheckResponse]);

  const emailOnChange = onChange => emailValue => {
    setUserCheckResponse(undefined);
    if (timeoutThrottle) {
      clearTimeout(timeoutThrottle);
    }
    timeoutThrottle = setTimeout(() => checkEmail(emailValue.target.value.trim()), EMAIL_CHECK_THROTTLE_MS);

    return onChange(emailValue);
  };

  const isExistingUser = (userCheckResponse !== undefined && userCheckResponse.result === 'existing') || userIsLogged;
  const hasNoPhone = !isExistingUser // New user
    || (userHasCheckResponse && !userCheckResponse?.has_phone) // Checked user with email, and he has no phone
    || (userIsLogged && !Boolean(user.phone)); // Logged but has no phone
  const hasNoMarketingConsent = ((isExistingUser && !userCheckResponse?.marketing_consent_granted) || !isExistingUser);

  useEffect(() => {
    setValue('existingUser', isExistingUser);
  }, [isExistingUser]);

  useEffect(() => {
    if (loggedContactEnabled && user) {
      const values = watch();

      // Trigger validation when fields are not empty
      if (Boolean(values.phone) && Boolean(values.email)) {
        trigger('email');
        trigger('phone');
      }
    }
  }, [user]);

  return <div>
    {(loggedContactEnabled || !userIsLogged) &&
      <ModalTextInputItem>
        <CustomLabel id="email" title={t('T.POST-JOB.EMAIL LABEL')} short />
        <CustomInput
          id="email"
          name="email"
          contentOnRight
          icon="envelope-filled"
          input={{ ...fieldProps, onChange: emailOnChange(fieldProps.onChange) }}
          placeholder={t('T.POST-JOB.EMAIL PLACEHOLDER')}
          disabled={Boolean(userFromStorage?.id)}
          meta={{
            error: errors?.email?.message as string,
            touched: touchedFields.email
          }}
        />
      </ModalTextInputItem>
    }
    {(
        (loggedContactEnabled && userIsLogged)
        || (userHasCheckResponse && !isExistingUser)
        || (userHasCheckResponse && hasNoPhone)
        || (userIsLogged && hasNoPhone)
      ) &&
      <>
        {(
          loggedContactEnabled
          || (userIsLogged && hasNoPhone)
          || (!userIsLogged && !isExistingUser)
          || (isExistingUser && hasNoPhone)
        ) && (
          <ModalTextInputItem>
            <CustomLabel id="phone"
                         title={t('T.POST-JOB.PHONE LABEL')}
                         subtitle={autoDealIds?.length > 1 ? t('T.POST-JOB.PHONE SUBTITLE MULTI') : t('T.POST-JOB.PHONE SUBTITLE')}
                         short
            />
            <CustomInput id="phone"
                         name="phone"
                         placeholder={t('T.POST-JOB.PHONE PLACEHOLDER')}
                         input={{ ...register('phone', {
                             required: t('T.POST-JOB.PHONE REQUIRED'),
                             validate: rhfValidateFn(phoneNumber, t),
                             onChange: val => {
                               const validatePhone = rhfValidateFn(phoneNumber);
                               if (validatePhone(val.target.value) === true) {
                                 trigger('phone');
                               }
                             }
                           })}}
                         meta={{
                           error: errors?.phone?.message as string,
                           touched: touchedFields.phone
                         }}
                         contentOnRight
                         icon="mobi-phone"
            />
          </ModalTextInputItem>
        )}
        {(
          (loggedContactEnabled && userIsLogged)
          || (!userIsLogged && !isExistingUser)
        ) && (
          <ModalTextInputItem>
            <CustomLabel id="name" title={t('T.POST-JOB.NAME LABEL')} short />
            <CustomInput id="name"
                         name="name"
                         placeholder={t('T.POST-JOB.NAME PLACEHOLDER')}
                         input={{ ...register('name', {
                           required: t('T.POST-JOB.NAME REQUIRED'),
                           onChange: () => trigger('name')
                         }) }}
                         meta={{
                           error: errors?.name?.message as string,
                           touched: touchedFields.name
                         }}
            />
          </ModalTextInputItem>
        )}
        {userHasCheckResponse && hasNoMarketingConsent && <ModalSelectionItem>
          <Controller
            name="marketing"
            control={control}
            render={controlInput => {
              return <ToggleField input={{ ...controlInput.field,
                title: t('T.POST-JOB.marketing LABEL'),
                onChange: controlInput.field.onChange }
              } toggleOnTheLeft />;
            }}
          />
        </ModalSelectionItem>}
        {userHasCheckResponse && !isExistingUser && !userIsLogged &&
          <div styleName="terms-wrapper">
            <label styleName={classNames({
              'terms-toggle-container': true,
              'terms-toggle-container--checked': tosOpen
            })}>
              <div styleName="terms-arrow" onClick={() => setTosOpen(!tosOpen)}>
                {t('T.POST-JOB.TERMS AGREEMENT')}
              </div>
              <div styleName="terms-text" dangerouslySetInnerHTML={{ __html: categoriesTos }} />
            </label>
          </div>
        }
      </>
    }

  </div>;
};

export default UserStep;
